import { useEffect, useState, memo, useCallback } from 'react';
import { Box, Typography, Button } from '@mui/material';
import { DataGrid, GridColDef, GridRenderCellParams } from '@mui/x-data-grid';

import Add from './Add';
import Edit from './Edit';
import UpdateStatus from './UpdateStatus';
import { getAllExpertAnswers } from '../../api/ExpertAnswers';

interface IExpertAnswer {
  id: number;
  type: string;
  status: string;
  title: string;
  description: string;
  author: string;
  created_at: string;
  updated_at: string;
}

const ExpertAnswers = () => {
  const [expertAnswers, setExpertAnswers] = useState<IExpertAnswer[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showUpdateStatusDialog, setShowUpdateStatusDialog] = useState(false);
  const [id, setId] = useState<number>(0);
  const [expertAnswerEntry, setExpertAnswerEntry] = useState<IExpertAnswer | null>(null);

  const fetchAllExpertAnswers = useCallback(async () => {
    try {
      setIsLoading(true);
      const response = await getAllExpertAnswers();

      if (response?.status !== 200 || !response?.data?.body) {
        return;
      }

      setExpertAnswers(response?.data?.body || []);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error fetching:', error);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAllExpertAnswers();
  }, []);

  const openEditDialog = useCallback((entryId: number) => {
    setId(entryId);
    setShowEditDialog(true);
  }, []);

  const openUpdateStatusDialog = useCallback((entry: IExpertAnswer) => {
    setExpertAnswerEntry(entry);
    setShowUpdateStatusDialog(true);
  }, []);

  const handleSubmitRefetch = () => {
    fetchAllExpertAnswers();
  };

  const columns: GridColDef[] = [
    {
      field: 'id',
      headerName: 'Id',
      width: 45
    },
    {
      field: 'type',
      headerName: 'Type',
      width: 125
    },
    {
      field: 'status',
      headerName: 'Status',
      width: 110
    },
    {
      field: 'title',
      headerName: 'Title',
      minWidth: 175,
      flex: 1
    },
    {
      field: 'author',
      headerName: 'Author',
      width: 175
    },
    {
      field: 'created_at',
      headerName: 'Created At',
      width: 100
    },
    {
      field: 'updated_at',
      headerName: 'Updated At',
      width: 100
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 300,
      renderCell: (params: GridRenderCellParams<any>) => {
        return (
          <>
            <Button
              variant='contained'
              size='small'
              sx={{ ml: '2px' }}
              onClick={() => {
                openEditDialog(params.row.id);
              }}>
              <Typography>Edit</Typography>
            </Button>
            <Button
              variant='contained'
              size='small'
              sx={{ ml: '2px' }}
              onClick={() => {
                openUpdateStatusDialog(params.row);
              }}>
              <Typography>Update Status</Typography>
            </Button>
          </>
        );
      }
    }
  ];

  return (
    <Box mt={10}>
      <Typography textAlign='center' my={3}>
        Expert Answers
      </Typography>
      <Box textAlign='right' mr='16px'>
        <Button variant='contained' onClick={() => setShowAddDialog(true)}>
          Add
        </Button>
      </Box>
      <Box height='80vh' px={2}>
        <DataGrid
          rows={expertAnswers}
          columns={columns}
          getRowId={row => row.id}
          rowSpacingType='margin'
          getRowHeight={() => 'auto'}
          loading={isLoading}
        />
      </Box>

      <Add
        showAddDialog={showAddDialog}
        closeAddDialog={() => setShowAddDialog(false)}
        handleSubmitRefetch={handleSubmitRefetch}
      />

      <Edit
        showEditDialog={showEditDialog}
        closeEditDialog={() => setShowEditDialog(false)}
        recordId={id}
        handleSubmitRefetch={handleSubmitRefetch}
      />

      <UpdateStatus
        open={showUpdateStatusDialog}
        onClose={() => setShowUpdateStatusDialog(false)}
        entry={expertAnswerEntry}
        onSuccess={handleSubmitRefetch}
      />
    </Box>
  );
};

export default memo(ExpertAnswers);
